<template>
  <v-container class="correlated-items">
    <!-- Título no mesmo padrão de Exames/Prescrições -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title" data-tour="co-cabecalho">
          <v-icon color="primary" class="mr-2">mdi-link-variant</v-icon>
          Itens Correlacionados
        </h3>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Botão para atualizar a lista -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              class="ml-2"
              @click="load"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="40" />
    </div>

    <!-- Vazio -->
    <div v-else-if="groups.length === 0" class="ci-empty">
      <v-icon size="56" color="grey lighten-1">mdi-link-off</v-icon>
      <p class="body-1 grey--text mt-3 mb-0">
        Nenhum item correlacionado ainda.
      </p>
      <p class="caption grey--text">
        Vincule informações pelo ícone 🔗 nos cards do paciente.
      </p>
    </div>

    <!-- Agrupado por assunto (só os que existem) -->
    <div v-else data-tour="co-lista">
      <div
        v-for="(group, gi) in groups"
        :key="group.type"
        class="ci-group"
      >
        <div class="ci-group-header" :style="{ borderColor: meta(group.type).color }">
          <v-icon small :color="meta(group.type).color" class="mr-2">
            {{ meta(group.type).icon }}
          </v-icon>
          <span class="ci-group-title" :style="{ color: meta(group.type).color }">
            {{ meta(group.type).label }}
          </span>
          <v-chip x-small class="ml-2" :color="meta(group.type).color" text-color="white">
            {{ group.items.length }}
          </v-chip>
        </div>

        <v-card
          v-for="(c, ci) in group.items"
          :key="c.correlation_id"
          outlined
          class="ci-row"
        >
          <div class="ci-row-body">
            <div class="ci-source">
              <strong v-if="c.source.code" class="ci-code mr-1">{{ c.source.code }}</strong>
              {{ c.source.label }}
            </div>
            <div class="ci-arrow">
              <v-icon x-small color="grey">mdi-arrow-right-thin</v-icon>
              <span v-if="c.relation_type" class="ci-rel">{{ c.relation_type }}</span>
            </div>
            <div class="ci-target">
              <v-chip
                x-small
                label
                :color="meta(c.target.type).color"
                text-color="white"
                class="mr-1"
              >
                {{ meta(c.target.type).label }}
              </v-chip>
              <strong v-if="c.target.code" class="ci-code mr-1">{{ c.target.code }}</strong>
              {{ c.target.label }}
            </div>
          </div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                color="error"
                class="ci-remove"
                :loading="removingId === c.correlation_id"
                @click="remove(c)"
                v-bind="attrs"
                v-on="on"
                :data-tour="gi === 0 && ci === 0 ? 'co-remover' : null"
              >
                <v-icon x-small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>Remover vínculo</span>
          </v-tooltip>
        </v-card>
      </div>
    </div>
  </v-container>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  getPatientCorrelations,
  unlinkCorrelation,
} from "@/services/correlationService";
import tourMixin from "@/features/tour/tourMixin";

// Metadados por tipo (rótulo, ícone, cor) para as seções.
const TYPE_META = {
  condition: { label: "CIDs", icon: "mdi-clipboard-pulse-outline", color: "#0E7490" },
  main_goal: { label: "Objetivo Principal", icon: "mdi-bullseye", color: "#EA580C" },
  goal: { label: "Metas e Prazos", icon: "mdi-flag-checkered", color: "#0D9488" },
  medication: { label: "Medicamentos", icon: "mdi-pill", color: "#15803D" },
  supplement: { label: "Suplementos", icon: "mdi-nutrition", color: "#B45309" },
  allergy: { label: "Alergias", icon: "mdi-alert-circle-outline", color: "#B91C1C" },
  symptom: { label: "Sintomas", icon: "mdi-emoticon-sick-outline", color: "#4F46E5" },
  restriction: { label: "Restrições", icon: "mdi-food-off", color: "#BE185D" },
  complementary_treatment: { label: "Trat. Complementares", icon: "mdi-leaf", color: "#7C3AED" },
  exam: { label: "Exames", icon: "mdi-test-tube", color: "#0369A1" },
  prescription_item: { label: "Prescrição", icon: "mdi-prescription", color: "#334155" },
};

export default {
  name: "CorrelatedItems",

  mixins: [tourMixin],

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      correlations: [],
      loading: false,
      removingId: null,
    };
  },

  computed: {
    tourId() {
      return "sec-correlacionados-pg";
    },

    patientId() {
      return this.$route.params.id;
    },

    // agrupa por tipo da ORIGEM; só grupos com itens; ordenados pela ordem do mapa.
    groups() {
      const byType = {};
      for (const c of this.correlations) {
        const t = c.source.type;
        (byType[t] = byType[t] || []).push(c);
      }
      return Object.keys(TYPE_META)
        .filter((t) => byType[t] && byType[t].length)
        .map((t) => ({ type: t, items: byType[t] }));
    },
  },

  created() {
    this.load();
  },

  methods: {
    tourReady() {
      return !this.loading;
    },

    tourSteps() {
      return [
        {
          title: "Itens correlacionados",
          description:
            "Esta aba reúne os vínculos entre as informações do paciente, como um medicamento que trata um CID.",
        },
        {
          anchor: "co-cabecalho",
          title: "O que são correlações",
          description:
            "Aqui você acompanha como as informações clínicas do paciente se conectam entre si.",
        },
        {
          anchor: "co-lista",
          title: "Vínculos por assunto",
          description:
            "As correlações ficam agrupadas por tipo, mostrando a origem, a relação e o item de destino.",
        },
        {
          anchor: "co-remover",
          title: "Remover vínculo",
          description:
            "Não precisa mais de um vínculo? Use este botão para removê-lo. Novos vínculos são criados pelo ícone 🔗 nos cards do paciente.",
        },
      ];
    },

    meta(type) {
      return TYPE_META[type] || { label: type, icon: "mdi-link-variant", color: "#64748B" };
    },

    async load() {
      if (!this.patientId) return;
      this.loading = true;
      try {
        this.correlations = await getPatientCorrelations(this.patientId);
      } catch (err) {
        console.error("Erro ao carregar correlações:", err);
        this.correlations = [];
      } finally {
        this.loading = false;
      }
    },

    async remove(c) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Remover vínculo",
        message: `Tem certeza que deseja remover o vínculo entre "${c.source.label}" e "${c.target.label}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;

      this.removingId = c.correlation_id;
      try {
        await unlinkCorrelation(c.correlation_id);
        await this.load();
      } catch (err) {
        console.error("Erro ao remover correlação:", err);
        this.erroDeRequisicao(err, "Erro ao remover o vínculo");
      } finally {
        this.removingId = null;
      }
    },
  },
};
</script>

<style scoped>
.correlated-items {
  padding-bottom: 24px;
}
/* Mesmo título das outras seções (Exames/Prescrições) */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}
.ci-empty {
  text-align: center;
  padding: 48px 12px;
}
.ci-group {
  margin-bottom: 18px;
}
.ci-group-header {
  display: flex;
  align-items: center;
  padding: 6px 0 8px 0;
  border-bottom: 2px solid;
  margin-bottom: 8px;
}
.ci-group-title {
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.ci-row {
  border-radius: 10px !important;
  padding: 10px 8px 10px 12px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ci-row-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}
.ci-source {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ds-text-strong, #1c1917);
  word-break: break-word;
}
.ci-arrow {
  display: flex;
  align-items: center;
  gap: 4px;
}
.ci-rel {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ds-text-muted, var(--ds-text-muted));
}
.ci-target {
  font-size: 13px;
  color: var(--ds-text-700, #44403c);
  word-break: break-word;
}
.ci-code {
  color: var(--ds-primary, var(--ds-primary));
}
.ci-remove {
  flex-shrink: 0;
}
</style>
